import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './PreviewModal.css';
import api from '../services/api';

function PreviewModal({ isOpen, onClose, onConfirm, mailingData }) {
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadManagers();
    }
  }, [isOpen]);

  const loadManagers = async () => {
    try {
      const data = await api.getManagers();
      setManagers(data);
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const handleTestSend = async () => {
    if (!selectedManager) {
      alert('Выберите менеджера для тестовой отправки');
      return;
    }

    setIsSending(true);
    let tempMailingId = null;

    try {
      const telegramId = selectedManager.id_telegram || selectedManager.telegram_id;
      if (!telegramId) {
        alert('У выбранного менеджера нет Telegram ID');
        setIsSending(false);
        return;
      }

      let imagePath = null;

      // Если это существующая рассылка - используем поле image напрямую
      if (!mailingData.isNew && mailingData.image && typeof mailingData.image === 'string' && mailingData.image.startsWith('mailings/')) {
        imagePath = mailingData.image;
      }
      // Если это новая рассылка с файлом - создаём временный черновик
      else if (mailingData.isNew && mailingData.imageFile) {
        try {
          // Создаём временный черновик
          const tempMailing = await api.createMailing({
            name: `[TEMP TEST] ${mailingData.name}`,
            message: mailingData.message,
            target_classes: mailingData.targetClasses || [1],
            status: 'draft'
          });

          tempMailingId = tempMailing.id;

          // Загружаем изображение
          const formData = new FormData();
          formData.append('image', mailingData.imageFile);
          await api.uploadMailingImage(tempMailingId, formData);

          // Получаем обновленную рассылку с путём к изображению
          const updatedMailing = await api.getMailing(tempMailingId);
          imagePath = updatedMailing.image;
        } catch (uploadError) {
          console.error('Error creating temp mailing:', uploadError);
          alert('❌ Ошибка загрузки изображения для теста');
          setIsSending(false);
          return;
        }
      }

      const payload = {
        telegram_id: parseInt(telegramId),
        message: mailingData.message,
      };

      // Добавляем image только если он есть
      if (imagePath) {
        payload.image = imagePath;
      }

      console.log('Sending test mailing:', payload);
      await api.sendTestMailing(payload);
      alert(`✅ Тестовое сообщение отправлено ${selectedManager.name || telegramId}`);
      setShowTestDropdown(false);
      setSelectedManager(null);
    } catch (error) {
      console.error('Error sending test:', error);
      console.error('Error details:', error.response || error);
      alert(`❌ Ошибка отправки: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      // Удаляем временный черновик если он был создан
      if (tempMailingId) {
        try {
          await api.deleteMailing(tempMailingId);
          console.log('Temporary mailing deleted:', tempMailingId);
        } catch (deleteError) {
          console.warn('Failed to delete temp mailing:', deleteError);
        }
      }
      setIsSending(false);
    }
  };

  if (!mailingData) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Предпросмотр рассылки"
      size="large"
    >
      <div className="preview-modal-content">
        <div className="preview-info">
          <h4>Название рассылки:</h4>
          <p>{mailingData.name}</p>
        </div>

        <div className="preview-message-container">
          <h4>Сообщение:</h4>
          <div className="preview-message-box">
            {mailingData.image && (
              <div className="preview-image">
                <img
                  src={mailingData.image}
                  alt="Preview"
                  onError={(e) => {
                    // Если картинка не загружается, показываем текст
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{
                  display: 'none',
                  padding: '12px',
                  background: '#e5e7eb',
                  borderRadius: '6px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  🖼️ Изображение: {mailingData.imageName || mailingData.image}
                </div>
              </div>
            )}
            <div
              className="preview-message-text"
              dangerouslySetInnerHTML={{
                __html: mailingData.message.replace(/\n/g, '<br>')
              }}
            />
          </div>
        </div>

        <div className="preview-recipients">
          <p><strong>Получателей:</strong> {mailingData.recipientsCount || 0} человек</p>
          <p><strong>Целевые интересы:</strong> {mailingData.targetClassesNames || 'Не указаны'}</p>
        </div>

        <div className="preview-warning">
          <span>⚠️</span>
          <p>Проверьте сообщение перед отправкой. После запуска у вас будет 30 секунд, чтобы отменить рассылку.</p>
        </div>

        <div className="preview-test-section">
          <div className="test-controls">
            <button
              onClick={() => setShowTestDropdown(!showTestDropdown)}
              className="btn-test-toggle"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Тест
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`chevron ${showTestDropdown ? 'rotated' : ''}`}
              >
                <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {showTestDropdown && (
              <div className="test-dropdown">
                <div className="test-dropdown-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Выберите получателя теста</span>
                </div>
                <select
                  value={selectedManager?.id || ''}
                  onChange={(e) => {
                    const manager = managers.find(m => m.id === parseInt(e.target.value));
                    setSelectedManager(manager);
                  }}
                  className="test-manager-select"
                >
                  <option value="">Выберите менеджера...</option>
                  {managers.map(manager => {
                    const telegramId = manager.id_telegram || manager.telegram_id;
                    return (
                      <option key={manager.id} value={manager.id}>
                        {manager.name || `ID: ${manager.id}`} {telegramId ? `(TG: ${telegramId})` : '(нет TG)'}
                      </option>
                    );
                  })}
                </select>

                <button
                  onClick={handleTestSend}
                  disabled={!selectedManager || isSending}
                  className="btn-test-send"
                >
                  {isSending ? (
                    <>
                      <div className="spinner"></div>
                      Отправка...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Отправить тест
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-outline">
            Нет, вернуться к редактированию
          </button>
          <button onClick={onConfirm} className="btn btn-primary">
            Да, запустить рассылку
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default PreviewModal;
